import { useState, useEffect, useMemo, useCallback, Fragment } from "react";
import { useNavigate } from "react-router";
import { isAxiosError } from "axios";
import { getFullContributions } from "@/services/api/widget";
import { rebalancingApi } from "@/services/api/rebalancing.api";
import type { ContributionItem as BaseContributionItem } from "@/services/api/widget/types";
import { useOsActionsOptional } from "@/components/ui/os-layout/OsContext";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from "@/components/ui/table/Table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExpand,
  faCompress,
  faGrid2Plus,
  faLayerGroup,
  faCopy,
  faColumns,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { useNotification } from "@/context/NotificationContext";
import TimePeriodSelector, {
  type Period,
} from "@/components/TimePeriodSelector";
import { Button } from "@/components/ui/button/Button";
import { AddWidgetModal } from "@/components/dashboard/AddWidgetModal";
import { useDashboard } from "@/context/DashboardContext";
import { ContributionColumnSelector } from "./ContributionColumnSelector";
import {
  CONTRIBUTION_COLUMNS,
  DEFAULT_CONTRIBUTION_COLUMNS,
  loadContributionColumns,
  saveContributionColumns,
  filterVisibleContributionColumns,
  getContributionCellNumber,
  extractSnapshotTextColumns,
  getContributionCellText,
  getContributionColumnLabel,
  compareContributionSortValues,
  getContributionSortKey,
  isPerformanceColumn,
  isLeftAlignedContributionColumn,
  isSnapshotTextColumn,
  shouldShowContributionFooterDash,
  shouldTruncateContributionSecurityName,
} from "./contributionColumns";
import {
  fetchAvailableExposures,
  type ExposureType,
} from "@/services/api/widget/chart-options";
import { Tooltip } from "@/components/ui/tooltip/Tooltip";
import clsx from "clsx";
import {
  buildExposurePrimaryColumnDefs,
  extractExposureTypesFromColumnIds,
  mergeContributionColumnCatalog,
} from "./exposurePrimaryColumns";

type ContributionProductMapEntry = {
  product_id?: string;
  resource_id?: string;
  resource_type?: "portfolio" | "product" | "exposure_category";
  unmapped_holdings?: string[];
};

// Define the time range options
type TimeRange = "MTD" | "YTD" | "1Y" | "3Y" | "custom";

// Period options for the TimePeriodSelector
const CONTRIBUTION_PERIODS: Period[] = [
  { id: "MTD", label: "MTD", months: 0 },
  { id: "YTD", label: "YTD", months: 0 },
  { id: "1Y", label: "1Y", months: 12 },
  { id: "3Y", label: "3Y", months: 36 },
  { id: "custom", label: "Custom", months: 0 },
];

// Extend the ContributionItem type to include lookthrough and performance fields
interface ContributionItem extends BaseContributionItem {
  avg_weight?: number;
  start_weight?: number;
  end_weight?: number;
  start_value_portfolio_ccy?: number;
  end_value_portfolio_ccy?: number;
  effective_avg_weight?: number;
  effective_contribution?: number;
  lookthrough_depth?: number;
  is_portfolio_contribution?: boolean;
  is_expanded?: boolean;
  cumulative_local_return?: number;
  cumulative_fx_return?: number;
  cumulative_portfolio_return?: number;
  cumulative_benchmark_return?: number;
  cumulative_relative_return?: number;
  relative_contribution?: number;
  effective_relative_contribution?: number;
  resource_id?: string; // For navigation
  resource_type?: "product" | "portfolio"; // Standardized resource type
  isin?: string;
  product_currency?: string;
  unmapped_holdings?: string[];
  is_exposure_category_row?: boolean;
}

const CONTRIBUTIONS_HOLDING_CELL_CLASS = "align-top !py-1.5";
const CONTRIBUTIONS_TEXT_CELL_CLASS = "text-left align-top !py-1.5";
const CONTRIBUTIONS_NUMERIC_CELL_CLASS = "text-right align-top !py-1.5";
const CONTRIBUTIONS_FOOTER_CELL_CLASS = `${CONTRIBUTIONS_NUMERIC_CELL_CLASS} text-gray-900 dark:text-gray-100`;

interface PortfolioContributionsProps {
  portfolioId: string;
}

export function PortfolioContributions({
  portfolioId,
}: PortfolioContributionsProps) {
  // State for contributions data
  const [contributions, setContributions] = useState<ContributionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Add state for time range
  const [timeRange, setTimeRange] = useState<TimeRange>("MTD");

  // Add state for custom date range
  const [customDateRange, setCustomDateRange] = useState<[string, string]>([
    "",
    "",
  ]);

  // State for lookthrough functionality
  const [enableLookthrough, setEnableLookthrough] = useState(false);
  const [aggregateMode, setAggregateMode] = useState(false);
  const [hasPortfolioContributions, setHasPortfolioContributions] =
    useState(false);

  const [exposureColumnDefs, setExposureColumnDefs] = useState<
    ReturnType<typeof buildExposurePrimaryColumnDefs>
  >([]);
  const [availableExposures, setAvailableExposures] = useState<ExposureType[]>(
    []
  );
  const [contributionGroupBy, setContributionGroupBy] = useState("");
  const [showAllCategories, setShowAllCategories] = useState(false);
  const availableColumns = useMemo(
    () => mergeContributionColumnCatalog(CONTRIBUTION_COLUMNS, exposureColumnDefs),
    [exposureColumnDefs]
  );
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    DEFAULT_CONTRIBUTION_COLUMNS
  );
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);
  const [columnsReady, setColumnsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const exposures = await fetchAvailableExposures();
        if (cancelled) return;
        setAvailableExposures(exposures);
        const defs = buildExposurePrimaryColumnDefs(exposures);
        setExposureColumnDefs(defs);
        setSelectedColumns((current) => {
          const loaded = loadContributionColumns(
            mergeContributionColumnCatalog(CONTRIBUTION_COLUMNS, defs).map(
              (col) => col.id
            )
          );
          const preservedExposureColumns = current.filter((columnId) =>
            defs.some((col) => col.id === columnId)
          );
          const nonExposureColumns = loaded.filter(
            (columnId) => !columnId.endsWith("_primary")
          );
          return [...nonExposureColumns, ...preservedExposureColumns];
        });
      } catch {
        if (!cancelled) {
          setSelectedColumns(loadContributionColumns());
        }
      } finally {
        if (!cancelled) {
          setColumnsReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedExposureTypes = useMemo(
    () => extractExposureTypesFromColumnIds(selectedColumns),
    [selectedColumns]
  );
  const selectedSnapshotColumns = useMemo(
    () => extractSnapshotTextColumns(selectedColumns),
    [selectedColumns]
  );

  const includePerformance = useMemo(
    () => selectedColumns.some((columnId) => isPerformanceColumn(columnId)),
    [selectedColumns]
  );

  // State for frontend sorting
  const [sortColumn, setSortColumn] = useState<string>("contribution");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // State for adding to dashboard
  const { dashboards } = useDashboard();
  const [showAddWidgetModal, setShowAddWidgetModal] = useState(false);

  // Notification hook for copy feedback
  const { showSuccess } = useNotification();

  const [productInfoMap, setProductInfoMap] = useState<
    Record<string, { isin?: string; currency?: string }>
  >({});
  const [portfolioCurrency, setPortfolioCurrency] = useState<string | undefined>(
    undefined
  );
  const [holdingsOnly, setHoldingsOnly] = useState(false);
  const [contributionsDataThrough, setContributionsDataThrough] = useState<
    string | null
  >(null);

  // Backfill ISIN / currency for holding subtitles
  useEffect(() => {
    const ids = new Set<string>();
    contributions.forEach((item) => {
      if (item.is_exposure_category_row) return;
      if (!item.resource_id) return;
      if (!item.isin || !item.product_currency) ids.add(item.resource_id);
    });

    const missing = Array.from(ids).filter((id) => !productInfoMap[id]);
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      const info = await rebalancingApi.lookupProductInfo(missing);
      if (cancelled || !info) return;
      setProductInfoMap((prev) => {
        const next = { ...prev };
        Object.entries(info).forEach(([pid, v]) => {
          next[pid] = { isin: v?.isin, currency: v?.currency };
        });
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [contributions]);

  const getContributionMetaLine = useCallback(
    (item: ContributionItem): string | null => {
      const fallback = item.resource_id
        ? productInfoMap[item.resource_id]
        : undefined;
      const isin = item.isin || fallback?.isin;
      const currency = item.product_currency || fallback?.currency;
      const parts = [isin, currency].filter(Boolean);
      return parts.length > 0 ? parts.join(" · ") : null;
    },
    [productInfoMap]
  );

  // Persist the user's column selection/order locally.
  const handleColumnsChange = useCallback((columnIds: string[]) => {
    const nextColumns =
      columnIds.length > 0 ? columnIds : DEFAULT_CONTRIBUTION_COLUMNS;
    setSelectedColumns(nextColumns);
    saveContributionColumns(nextColumns);
  }, []);

  // Navigation hooks
  const navigate = useNavigate();
  const osContext = useOsActionsOptional();

  // Handle row click for navigation
  const handleRowClick = (item: ContributionItem) => {
    if (item.is_exposure_category_row || !item.resource_id) {
      return;
    }

    // If in OS mode, open in a new window
    if (osContext) {
      if (item.resource_type === "portfolio" || item.is_portfolio_contribution) {
        osContext.openWindow({
          appId: 'portfolio',
          title: item.name || 'Portfolio',
          params: { id: item.resource_id },
        });
      } else {
        osContext.openWindow({
          appId: 'product',
          title: item.name || 'Product',
          params: { id: item.resource_id },
        });
      }
    } else {
      // Classic layout - use React Router navigation
      if (item.resource_type === "portfolio" || item.is_portfolio_contribution) {
        navigate(`/portfolio/${item.resource_id}`);
      } else {
        navigate(`/product/${item.resource_id}`);
      }
    }
  };

  // Handle column sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  // Handle copy to clipboard for Excel
  const handleCopyToClipboard = () => {
    const tableElement = document.querySelector("[data-contributions-table]");
    if (tableElement) {
      // Get all rows including header
      const rows = tableElement.querySelectorAll("tr");
      const data: string[][] = [];

      rows.forEach((row) => {
        const cells = row.querySelectorAll("th, td");
        const rowData: string[] = [];
        cells.forEach((cell) => {
          rowData.push(cell.textContent?.trim() || "");
        });
        data.push(rowData);
      });

      // Convert to tab-delimited format for Excel
      const tsvContent = data.map((row) => row.join("\t")).join("\n");

      navigator.clipboard.writeText(tsvContent).then(() => {
        showSuccess(
          "Copied to clipboard",
          "Paste into Excel or any spreadsheet"
        );
      });
    }
  };

  // Fetch contributions data
  useEffect(() => {
    let cancelled = false;

    async function fetchContributionsData() {
      if (!portfolioId || !columnsReady) return;

      // If custom is selected but dates aren't set, don't fetch yet
      if (
        timeRange === "custom" &&
        (!customDateRange[0] || !customDateRange[1])
      ) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const fullResponse = await getFullContributions({
          portfolio_id: portfolioId,
          time_range:
            timeRange === "custom"
              ? {
                start_date: customDateRange[0],
                end_date: customDateRange[1],
              }
              : {
                preset: timeRange as Exclude<TimeRange, "custom">,
              },
          include_weights: true,
          include_performance: includePerformance,
          exposure_columns:
            selectedExposureTypes.length > 0 ? selectedExposureTypes : undefined,
          snapshot_columns:
            selectedSnapshotColumns.length > 0
              ? selectedSnapshotColumns
              : undefined,
          sort_by: "contribution",
          sort_direction: "desc",
          enable_lookthrough: enableLookthrough,
          max_lookthrough_depth: 1,
          contribution_group_by: contributionGroupBy || undefined,
          show_all_categories: contributionGroupBy ? showAllCategories : undefined,
        });

        if (cancelled) return;

        const resourceData = fullResponse.data.table;
        const metadata = fullResponse.metadata;
        const productMaps =
          (metadata.product_id_map as Record<string, ContributionProductMapEntry>) ||
          {};
        setPortfolioCurrency(metadata.portfolio_currency);
        setHoldingsOnly(Boolean(metadata.holdings_only));
        setContributionsDataThrough(metadata.contributions_data_through ?? null);

        if (
          resourceData &&
          Array.isArray(resourceData.cols) &&
          Array.isArray(resourceData.rows)
        ) {
          const nameColIndex = resourceData.cols.findIndex(
            (col: any) => col.id === "name"
          );
          const contributionColIndex = resourceData.cols.findIndex(
            (col: any) => col.id === "contribution"
          );
          const weightColIndex = resourceData.cols.findIndex(
            (col: any) => col.id === "weight"
          );
          const avgWeightColIndex = resourceData.cols.findIndex(
            (col: any) => col.id === "avg_weight"
          );
          const startWeightColIndex = resourceData.cols.findIndex(
            (col: any) => col.id === "start_weight"
          );
          const endWeightColIndex = resourceData.cols.findIndex(
            (col: any) => col.id === "end_weight"
          );
          const startValueColIndex = resourceData.cols.findIndex(
            (col: any) => col.id === "start_value_portfolio_ccy"
          );
          const endValueColIndex = resourceData.cols.findIndex(
            (col: any) => col.id === "end_value_portfolio_ccy"
          );
          const effectiveAvgWeightColIndex = resourceData.cols.findIndex(
            (col: any) => col.id === "effective_avg_weight"
          );
          const effectiveContributionColIndex = resourceData.cols.findIndex(
            (col: any) => col.id === "effective_contribution"
          );
          const lookthoughDepthColIndex = resourceData.cols.findIndex(
            (col: any) => col.id === "lookthrough_depth"
          );
          const isPortfolioContributionColIndex = resourceData.cols.findIndex(
            (col: any) => col.id === "is_portfolio_contribution"
          );

          // Performance column indexes
          const cumulativeLocalReturnColIndex = resourceData.cols.findIndex(
            (col: any) => col.id === "cumulative_local_return"
          );
          const cumulativeFxReturnColIndex = resourceData.cols.findIndex(
            (col: any) => col.id === "cumulative_fx_return"
          );
          const cumulativePortfolioReturnColIndex = resourceData.cols.findIndex(
            (col: any) => col.id === "cumulative_portfolio_return"
          );
          const cumulativeBenchmarkReturnColIndex = resourceData.cols.findIndex(
            (col: any) => col.id === "cumulative_benchmark_return"
          );
          const cumulativeRelativeReturnColIndex = resourceData.cols.findIndex(
            (col: any) => col.id === "cumulative_relative_return"
          );
          const relativeContributionColIndex = resourceData.cols.findIndex(
            (col: any) => col.id === "relative_contribution"
          );
          const effectiveRelativeContributionColIndex =
            resourceData.cols.findIndex(
              (col: any) => col.id === "effective_relative_contribution"
            );

          if (nameColIndex !== -1 && contributionColIndex !== -1) {
            const transformedContributions = resourceData.rows
              .map((row: any, index: number) => {
                const item: ContributionItem = {
                  id: `contribution-${index}`,
                  name: row[nameColIndex] as string,
                  contribution: row[contributionColIndex] as number,
                };

                // Add weight if available
                if (weightColIndex !== -1) {
                  item.weight = row[weightColIndex] as number;
                }

                // Add average weight if available
                if (avgWeightColIndex !== -1) {
                  item.avg_weight = row[avgWeightColIndex] as number;
                }

                if (startWeightColIndex !== -1) {
                  item.start_weight = row[startWeightColIndex] as number;
                }

                if (endWeightColIndex !== -1) {
                  item.end_weight = row[endWeightColIndex] as number;
                }

                if (startValueColIndex !== -1) {
                  item.start_value_portfolio_ccy = row[
                    startValueColIndex
                  ] as number;
                }

                if (endValueColIndex !== -1) {
                  item.end_value_portfolio_ccy = row[endValueColIndex] as number;
                }

                // Add effective average weight if available
                if (effectiveAvgWeightColIndex !== -1) {
                  item.effective_avg_weight = row[
                    effectiveAvgWeightColIndex
                  ] as number;
                }

                // Add effective contribution if available
                if (effectiveContributionColIndex !== -1) {
                  item.effective_contribution = row[
                    effectiveContributionColIndex
                  ] as number;
                }

                if (lookthoughDepthColIndex !== -1) {
                  item.lookthrough_depth = row[
                    lookthoughDepthColIndex
                  ] as number;
                  item.is_expanded =
                    (row[lookthoughDepthColIndex] as number) > 0;
                }

                if (isPortfolioContributionColIndex !== -1) {
                  item.is_portfolio_contribution =
                    row[isPortfolioContributionColIndex] === "Yes";
                }

                // Add performance metrics if available
                if (cumulativeLocalReturnColIndex !== -1) {
                  item.cumulative_local_return = row[
                    cumulativeLocalReturnColIndex
                  ] as number;
                }
                if (cumulativeFxReturnColIndex !== -1) {
                  item.cumulative_fx_return = row[
                    cumulativeFxReturnColIndex
                  ] as number;
                }
                if (cumulativePortfolioReturnColIndex !== -1) {
                  item.cumulative_portfolio_return = row[
                    cumulativePortfolioReturnColIndex
                  ] as number;
                }
                if (cumulativeBenchmarkReturnColIndex !== -1) {
                  item.cumulative_benchmark_return = row[
                    cumulativeBenchmarkReturnColIndex
                  ] as number;
                }
                if (cumulativeRelativeReturnColIndex !== -1) {
                  item.cumulative_relative_return = row[
                    cumulativeRelativeReturnColIndex
                  ] as number;
                }
                if (relativeContributionColIndex !== -1) {
                  item.relative_contribution = row[
                    relativeContributionColIndex
                  ] as number;
                }

                // Add effective relative contribution if available (for lookthrough)
                if (effectiveRelativeContributionColIndex !== -1) {
                  item.effective_relative_contribution = row[
                    effectiveRelativeContributionColIndex
                  ] as number;
                }

                // Add resource_id and resource_type from product map
                const productInfo = productMaps[`row_${index}`];
                if (productInfo?.product_id) {
                  item.resource_id =
                    productInfo.resource_id || productInfo.product_id;
                  item.resource_type =
                    productInfo.resource_type ||
                    (productInfo.product_id?.startsWith("port-")
                      ? "portfolio"
                      : "product");
                  if (productInfo.resource_type === "exposure_category") {
                    item.is_exposure_category_row = true;
                  }
                  if (productInfo.unmapped_holdings?.length) {
                    item.unmapped_holdings = productInfo.unmapped_holdings;
                  }
                }

                resourceData.cols.forEach((col: { id: string }, colIndex: number) => {
                  if (isSnapshotTextColumn(col.id)) {
                    const value = row[colIndex];
                    item[col.id] =
                      value === null || value === undefined ? "" : String(value);
                  }
                });

                return item;
              })
              .filter((item: any): item is ContributionItem => item !== null);

            if (!cancelled) {
              setContributions(transformedContributions);

              // Check if any contributions are portfolio contributions (to show/hide lookthrough toggle)
              const hasPortfolios = transformedContributions.some(
                (contribution: ContributionItem) =>
                  contribution.resource_type === "portfolio" ||
                  contribution.is_portfolio_contribution
              );
              setHasPortfolioContributions(hasPortfolios);
            }
          } else if (!cancelled) {
            setError("Required column data not found");
          }
        } else if (!cancelled) {
          setContributions([]);
        }
      } catch (error) {
        if (!cancelled) {
          const isTimeout =
            isAxiosError(error) && error.response?.status === 504;
          const message =
            error instanceof Error
              ? error.message
              : "Failed to fetch contributions data";
          if (isTimeout) {
            setError(
              "Contributions request timed out. Try a shorter date range or fewer columns."
            );
          } else {
            setError(message);
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchContributionsData();

    return () => {
      cancelled = true;
    };
  }, [
    portfolioId,
    columnsReady,
    timeRange,
    enableLookthrough,
    includePerformance,
    customDateRange,
    selectedExposureTypes,
    selectedSnapshotColumns,
    contributionGroupBy,
    showAllCategories,
  ]);

  // Aggregate contributions when in aggregate mode (combine duplicate products across portfolios)
  const aggregatedContributions = useMemo(() => {
    if (!contributions || !enableLookthrough || !aggregateMode) {
      return contributions;
    }

    // Filter to only include product contributions (not portfolio holdings)
    // and aggregate by resource_id
    const productContributions = contributions.filter(
      (c) => c.resource_type !== "portfolio" && !c.is_portfolio_contribution
    );

    if (productContributions.length === 0) {
      return contributions;
    }

    // Group by resource_id (or name as fallback)
    const aggregated = new Map<string, ContributionItem>();

    for (const contrib of productContributions) {
      const key = contrib.resource_id || contrib.name;

      if (aggregated.has(key)) {
        const existing = aggregated.get(key)!;
        // Sum the contributions and weights
        existing.contribution =
          (existing.contribution || 0) + (contrib.contribution || 0);
        existing.effective_contribution =
          (existing.effective_contribution || 0) +
          (contrib.effective_contribution || 0);
        existing.avg_weight =
          (existing.avg_weight || 0) + (contrib.avg_weight || 0);
        existing.effective_avg_weight =
          (existing.effective_avg_weight || 0) +
          (contrib.effective_avg_weight || 0);
        existing.weight = (existing.weight || 0) + (contrib.weight || 0);
        existing.relative_contribution =
          (existing.relative_contribution || 0) +
          (contrib.relative_contribution || 0);
        existing.effective_relative_contribution =
          (existing.effective_relative_contribution || 0) +
          (contrib.effective_relative_contribution || 0);
        existing.start_weight =
          (existing.start_weight || 0) + (contrib.start_weight || 0);
        existing.end_weight =
          (existing.end_weight || 0) + (contrib.end_weight || 0);
        existing.start_value_portfolio_ccy =
          (existing.start_value_portfolio_ccy || 0) +
          (contrib.start_value_portfolio_ccy || 0);
        existing.end_value_portfolio_ccy =
          (existing.end_value_portfolio_ccy || 0) +
          (contrib.end_value_portfolio_ccy || 0);
      } else {
        // Clone the contribution to avoid mutating the original
        aggregated.set(key, {
          ...contrib,
          lookthrough_depth: 0,
          is_expanded: false,
        });
      }
    }

    return Array.from(aggregated.values());
  }, [contributions, enableLookthrough, aggregateMode]);

  // Sort contributions based on current sort state with hierarchical support for lookthrough
  const sortedContributions = useMemo(() => {
    const dataToSort = aggregateMode ? aggregatedContributions : contributions;
    if (!dataToSort || dataToSort.length === 0) return [];

    const compareValues = (a: ContributionItem, b: ContributionItem) =>
      compareContributionSortValues(
        a,
        b,
        sortColumn,
        sortDirection,
        enableLookthrough,
        aggregateMode
      );

    // If aggregate mode is on, just do a simple sort (no hierarchy)
    if (aggregateMode) {
      return [...dataToSort].sort(compareValues);
    }

    // If lookthrough is disabled or we don't have any expanded items, use simple sort
    const hasExpandedItems = dataToSort.some(
      (c) => (c.lookthrough_depth || 0) > 0
    );
    if (!enableLookthrough || !hasExpandedItems) {
      return [...dataToSort].sort(compareValues);
    }

    // For lookthrough mode (non-aggregated), we need hierarchical sorting
    const result: ContributionItem[] = [];
    const processed = new Set<string>();

    // First, collect and sort all top-level items (depth 0)
    const topLevel = dataToSort.filter((c) => (c.lookthrough_depth || 0) === 0);
    topLevel.sort(compareValues);

    // Process each top-level item and its children
    for (const parent of topLevel) {
      result.push(parent);
      processed.add(parent.id);

      // Find the index of this parent in the original array
      const parentIndex = dataToSort.findIndex((c) => c.id === parent.id);

      // Collect children that follow this parent
      const children: ContributionItem[] = [];
      for (let i = parentIndex + 1; i < dataToSort.length; i++) {
        const item = dataToSort[i];
        if ((item.lookthrough_depth || 0) === 0) break; // Hit another parent
        if ((item.lookthrough_depth || 0) > 0 && !processed.has(item.id)) {
          children.push(item);
          processed.add(item.id);
        }
      }

      // Sort children within this parent group
      children.sort(compareValues);

      // Add sorted children to result
      result.push(...children);
    }

    return result;
  }, [
    contributions,
    aggregatedContributions,
    aggregateMode,
    sortColumn,
    sortDirection,
    enableLookthrough,
  ]);

  // Helper function to format percentage with color
  const formatPerformance = (value: number | undefined) => {
    if (value === undefined || value === null) return "0.00";
    const formatted = value.toFixed(2);
    const isPositive = value > 0;
    const isNegative = value < 0;

    return (
      <span
        className={
          isPositive
            ? "text-green-600 dark:text-green-400"
            : isNegative
              ? "text-red-600 dark:text-red-400"
              : ""
        }
      >
        {formatted}%
      </span>
    );
  };

  const formatCurrencyValue = (value: number | undefined) => {
    if (value === undefined || value === null) return "0.00";
    if (portfolioCurrency) {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: portfolioCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    }
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const hasWeightColumns = contributions[0]?.start_weight !== undefined;
  const hasCurrentWeight = contributions[0]?.weight !== undefined;
  const visibleColumns = useMemo(
    () =>
      filterVisibleContributionColumns(selectedColumns, {
        hasWeightColumns,
        hasCurrentWeight,
      }),
    [selectedColumns, hasWeightColumns, hasCurrentWeight]
  );
  const shouldTruncateSecurityName = shouldTruncateContributionSecurityName(
    visibleColumns.length
  );

  const footerContributions = (aggregateMode ? sortedContributions : contributions).filter(
    (item) =>
      aggregateMode ||
      !enableLookthrough ||
      (item.lookthrough_depth || 0) === 0
  );

  const renderContributionCell = (
    item: ContributionItem,
    columnId: string
  ) => {
    if (isSnapshotTextColumn(columnId)) {
      return getContributionCellText(item, columnId) ?? "—";
    }

    const value = getContributionCellNumber(
      item,
      columnId,
      enableLookthrough,
      aggregateMode
    );

    if (
      columnId === "start_value_portfolio_ccy" ||
      columnId === "end_value_portfolio_ccy"
    ) {
      return formatCurrencyValue(value);
    }

    if (
      columnId === "contribution" ||
      columnId === "relative_contribution" ||
      isPerformanceColumn(columnId)
    ) {
      return formatPerformance(value ?? 0);
    }

    return `${(value ?? 0).toFixed(2)}%`;
  };

  const getContributionCellClassName = (columnId: string) => {
    if (columnId === "name") {
      return CONTRIBUTIONS_HOLDING_CELL_CLASS;
    }
    if (isLeftAlignedContributionColumn(columnId)) {
      return CONTRIBUTIONS_TEXT_CELL_CLASS;
    }
    return CONTRIBUTIONS_NUMERIC_CELL_CLASS;
  };

  const renderSecurityNameCell = (item: ContributionItem) => {
    const contributionMetaLine = getContributionMetaLine(item);

    return (
      <div
        style={
          enableLookthrough &&
          item.lookthrough_depth !== undefined &&
          item.lookthrough_depth > 0
            ? {
                marginLeft: `${item.lookthrough_depth * 12}px`,
              }
            : undefined
        }
      >
        <div
          className={`flex items-start ${
            shouldTruncateSecurityName ? "min-w-0" : ""
          }`}
        >
          {enableLookthrough &&
            item.lookthrough_depth !== undefined &&
            item.lookthrough_depth > 0 && (
              <span className="mr-2 text-xs text-gray-400 flex-shrink-0 leading-tight">
                ↳
              </span>
            )}
          <div
            className={`${
              shouldTruncateSecurityName ? "min-w-0" : ""
            } leading-tight`}
          >
            <div
              className={`flex items-baseline gap-1 ${
                shouldTruncateSecurityName ? "min-w-0" : ""
              }`}
            >
              {item.name === "Unmapped" &&
              item.unmapped_holdings &&
              item.unmapped_holdings.length > 0 ? (
                <Tooltip
                  content={
                    <div className="max-w-xs">
                      <p className="font-semibold mb-1">
                        Holdings without data for this exposure group:
                      </p>
                      <ul className="list-disc pl-4 space-y-0.5">
                        {item.unmapped_holdings.map((holdingName) => (
                          <li key={holdingName}>{holdingName}</li>
                        ))}
                      </ul>
                    </div>
                  }
                >
                  <span
                    className={`${
                      shouldTruncateSecurityName
                        ? "truncate inline-block max-w-[300px]"
                        : "whitespace-normal"
                    } font-medium text-gray-900 dark:text-white underline decoration-dotted cursor-help`}
                    title={item.name}
                  >
                    {item.name}
                  </span>
                </Tooltip>
              ) : (
                <span
                  className={`${
                    shouldTruncateSecurityName
                      ? "truncate inline-block max-w-[300px]"
                      : "whitespace-normal"
                  } ${
                    item.is_exposure_category_row
                      ? "font-medium text-gray-900 dark:text-white"
                      : "hover:text-indigo-500 hover:underline cursor-pointer transition-colors duration-150"
                  } ${
                    !item.is_expanded && item.is_portfolio_contribution
                      ? "font-semibold text-gray-900 dark:text-gray-100"
                      : "font-medium text-gray-900 dark:text-white"
                  }`}
                  title={item.name}
                  onClick={() => handleRowClick(item)}
                >
                  {item.name}
                </span>
              )}
              {(item.resource_type === "portfolio" ||
                item.is_portfolio_contribution) && (
                <span className="px-1 py-0 text-[10px] leading-none bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded flex-shrink-0">
                  Portfolio
                </span>
              )}
            </div>
            {contributionMetaLine && (
              <div
                className={`text-[11px] leading-tight text-gray-500 dark:text-gray-400 mt-0.5 ${
                  shouldTruncateSecurityName ? "truncate" : "whitespace-normal"
                }`}
              >
                {contributionMetaLine}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderContributionFooterCell = (columnId: string) => {
    if (isSnapshotTextColumn(columnId) || isLeftAlignedContributionColumn(columnId)) {
      return (
        <TableCell className={getContributionCellClassName(columnId)} />
      );
    }

    if (shouldShowContributionFooterDash(columnId)) {
      return (
        <TableCell
          className={`${CONTRIBUTIONS_NUMERIC_CELL_CLASS} text-gray-500`}
        >
          —
        </TableCell>
      );
    }

    if (columnId === "weight") {
      return (
        <TableCell className={CONTRIBUTIONS_FOOTER_CELL_CLASS}>
          {(aggregateMode ? sortedContributions : contributions)
            .filter(
              (item) =>
                aggregateMode ||
                !enableLookthrough ||
                (item.lookthrough_depth || 0) === 0
            )
            .reduce((sum, item) => sum + (item.weight || 0), 0)
            .toFixed(2)}
          %
        </TableCell>
      );
    }

    const total = footerContributions.reduce((sum, item) => {
      const value = getContributionCellNumber(
        item,
        columnId,
        enableLookthrough,
        aggregateMode
      );
      return sum + (value || 0);
    }, 0);

    if (
      columnId === "start_value_portfolio_ccy" ||
      columnId === "end_value_portfolio_ccy"
    ) {
      return (
        <TableCell className={CONTRIBUTIONS_FOOTER_CELL_CLASS}>
          {formatCurrencyValue(total)}
        </TableCell>
      );
    }

    if (
      columnId === "contribution" ||
      columnId === "relative_contribution"
    ) {
      return (
        <TableCell className={CONTRIBUTIONS_FOOTER_CELL_CLASS}>
          {formatPerformance(total)}
        </TableCell>
      );
    }

    return (
      <TableCell className={CONTRIBUTIONS_FOOTER_CELL_CLASS}>
        {total.toFixed(2)}%
      </TableCell>
    );
  };

  // Show waiting message if custom range selected but dates not yet picked
  const isWaitingForCustomDates =
    timeRange === "custom" && (!customDateRange[0] || !customDateRange[1]);

  if (loading && !isWaitingForCustomDates) {
    return (
      <div className="p-4 text-gray-700 dark:text-gray-300">
        Loading contributions data...
      </div>
    );
  }

  if (error) {
    return <div className="p-4 text-red-500">Error: {error}</div>;
  }

  if (contributions.length === 0 && !isWaitingForCustomDates) {
    const emptyMessage = contributionsDataThrough
      ? `No contribution data for this period. Holdings contributions are available through ${contributionsDataThrough}.`
      : "No contributions data available";
    return (
      <div className="w-full p-4">
        <TimePeriodSelector
          period={timeRange}
          setPeriod={(p) => setTimeRange(p as TimeRange)}
          periods={CONTRIBUTION_PERIODS}
          customDateRange={customDateRange}
          setCustomDateRange={setCustomDateRange}
          showDatePickerPresets={false}
          className="mb-4"
        />
        <div className="p-4 text-gray-700 dark:text-gray-300">{emptyMessage}</div>
      </div>
    );
  }

  // Show message when waiting for custom dates
  if (isWaitingForCustomDates && contributions.length === 0) {
    return (
      <div className="w-full p-4">
        <TimePeriodSelector
          period={timeRange}
          setPeriod={(p) => setTimeRange(p as TimeRange)}
          periods={CONTRIBUTION_PERIODS}
          customDateRange={customDateRange}
          setCustomDateRange={setCustomDateRange}
          showDatePickerPresets={false}
          className="mb-4"
        />
        <div className="p-4 text-gray-700 dark:text-gray-300 text-center">
          Please select both start and end dates to view contributions.
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-4">
      <TimePeriodSelector
        period={timeRange}
        setPeriod={(p) => setTimeRange(p as TimeRange)}
        periods={CONTRIBUTION_PERIODS}
        customDateRange={customDateRange}
        setCustomDateRange={setCustomDateRange}
        showDatePickerPresets={false}
        className="mb-4"
      />
      {holdingsOnly && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
          Contribution percentages are not available for this period. Showing
          holdings weights and values at the selected dates instead.
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-3 min-w-0">
          <Button
            color="zinc"
            onClick={() => setIsColumnSelectorOpen(true)}
            className="h-auto py-1"
            title="Configure columns"
          >
            <FontAwesomeIcon icon={faColumns} className="text-xs" />
            <span>Columns</span>
          </Button>
          {availableExposures.length > 0 && (
            <>
              <div className="flex items-center gap-2">
                <label
                  htmlFor="contributions-group-by"
                  className="text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap"
                >
                  Group by
                </label>
                <select
                  id="contributions-group-by"
                  value={contributionGroupBy}
                  onChange={(e) => setContributionGroupBy(e.target.value)}
                  className={clsx(
                    "text-sm rounded-lg border border-gray-200 dark:border-gray-600",
                    "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100",
                    "py-1.5 pl-2 pr-8 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                  )}
                >
                  <option value="">None</option>
                  {availableExposures.map((exp) => (
                    <option
                      key={exp.value}
                      value={exp.value.replace(/-/g, "_")}
                    >
                      {exp.label}
                    </option>
                  ))}
                </select>
              </div>
              {contributionGroupBy && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    Show all categories
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={showAllCategories}
                    onClick={() => setShowAllCategories(!showAllCategories)}
                    className={clsx(
                      "relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors",
                      showAllCategories ? "bg-indigo-500" : "bg-gray-300 dark:bg-gray-600"
                    )}
                  >
                    <span
                      className={clsx(
                        "inline-block h-4 w-4 transform rounded-full bg-white shadow transition",
                        showAllCategories ? "translate-x-4" : "translate-x-0"
                      )}
                    />
                  </button>
                </label>
              )}
            </>
          )}
        </div>
        <div className="flex flex-wrap justify-end gap-2">
        {hasPortfolioContributions && (
          <Button
            color="zinc"
            onClick={() => {
              const newLookthrough = !enableLookthrough;
              setEnableLookthrough(newLookthrough);
              if (!newLookthrough) {
                setAggregateMode(false);
              }
            }}
            className="h-auto py-1"
            title={
              enableLookthrough
                ? "Hide underlying contributions"
                : "Show underlying contributions"
            }
          >
            <FontAwesomeIcon
              icon={enableLookthrough ? faCompress : faExpand}
              className="text-xs"
            />
            <span>{enableLookthrough ? "Collapse" : "Lookthrough"}</span>
          </Button>
        )}
        {enableLookthrough && hasPortfolioContributions && (
          <Button
            color={aggregateMode ? "indigo" : "zinc"}
            onClick={() => setAggregateMode(!aggregateMode)}
            className="h-auto py-1"
            title={
              aggregateMode
                ? "Show hierarchical breakdown"
                : "Combine duplicate holdings"
            }
          >
            <FontAwesomeIcon icon={faLayerGroup} className="text-xs" />
            <span>Aggregate</span>
          </Button>
        )}
        <Button
          color="zinc"
          onClick={handleCopyToClipboard}
          className="h-auto py-1"
          title="Copy to clipboard (paste in Excel)"
        >
          <FontAwesomeIcon icon={faCopy} className="text-xs" />
          <span>Copy to Excel</span>
        </Button>
        <Button
          color="zinc"
          onClick={() => setShowAddWidgetModal(true)}
          className="h-auto py-1"
          title="Add to dashboard"
          icon={<FontAwesomeIcon icon={faGrid2Plus} />}
        >
          <span>Add to Dashboard</span>
        </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table dense striped className="min-w-full" data-contributions-table>
          <TableHead>
            <TableRow>
              {visibleColumns.map((columnId) => {
                const sortKey = getContributionSortKey(
                  columnId,
                  enableLookthrough
                );
                const isLeftAligned = isLeftAlignedContributionColumn(columnId);
                return (
                  <TableHeader
                    key={columnId}
                    className={`${
                      columnId === "name"
                        ? shouldTruncateSecurityName
                          ? "min-w-[200px]"
                          : "min-w-[280px]"
                        : isLeftAligned
                          ? "min-w-[120px]"
                          : "text-right min-w-[90px]"
                    } cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 whitespace-nowrap`}
                    onClick={() => handleSort(sortKey)}
                  >
                    <div
                      className={`flex items-center gap-1 ${
                        isLeftAligned ? "" : "justify-end"
                      }`}
                    >
                      {getContributionColumnLabel(
                        columnId,
                        enableLookthrough,
                        portfolioCurrency
                      )}
                      {sortColumn === sortKey && (
                        <span className="text-xs">
                          {sortDirection === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                  </TableHeader>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedContributions.map((item) => (
                <TableRow
                  key={item.id}
                  className={
                    item.is_expanded
                      ? ""
                      : item.is_portfolio_contribution
                        ? "bg-gray-50 dark:bg-gray-800/50"
                        : ""
                  }
                >
                  {visibleColumns.map((columnId) => (
                    <TableCell
                      key={`${item.id}-${columnId}`}
                      className={getContributionCellClassName(columnId)}
                    >
                      {columnId === "name"
                        ? renderSecurityNameCell(item)
                        : renderContributionCell(item, columnId)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}

            <TableRow className="font-semibold bg-gray-50 dark:bg-gray-800">
              {visibleColumns.map((columnId) => (
                <Fragment key={`footer-${columnId}`}>
                  {columnId === "name" ? (
                    <TableCell
                      className={`${getContributionCellClassName(columnId)} font-semibold`}
                    >
                      Total
                    </TableCell>
                  ) : (
                    renderContributionFooterCell(columnId)
                  )}
                </Fragment>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </div>

      {/* Footnote */}
      <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 italic">
        * Contributions may not add up to exact performance due to compounding
        effects.
        {includePerformance && (
          <>
            <br />* Performance metrics show cumulative returns over the
            selected period.
            <br />* Relative performance compares portfolio returns to benchmark
            returns.
            <br />* For products with incomplete daily data (e.g. monthly funds), 
            returns may include benchmark proxy performance for dates where actual data is unavailable.
          </>
        )}
      </div>

      {/* Add to Dashboard Modal */}
      <AddWidgetModal
        isOpen={showAddWidgetModal}
        onClose={() => setShowAddWidgetModal(false)}
        widgetType="contributions"
        initialConfig={{
          portfolio_id: portfolioId,
          time_range:
            timeRange === "custom" && customDateRange[0] && customDateRange[1]
              ? {
                start_date: customDateRange[0],
                end_date: customDateRange[1],
              }
              : {
                preset: timeRange,
              },
          include_weights: true,
          include_performance: includePerformance,
          enable_lookthrough: enableLookthrough,
        }}
      />

      <ContributionColumnSelector
        isOpen={isColumnSelectorOpen}
        onClose={() => setIsColumnSelectorOpen(false)}
        selectedColumns={selectedColumns}
        onColumnsChange={handleColumnsChange}
        availableColumns={availableColumns}
      />
    </div>
  );
}
